import {
    BxfIcon,
    BxfIconTypeEnum,
    BxfLink,
    BxfTargetAtrEnum,
} from '@blk/bxf-react-components';

import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import {
    ActionButtonRendererConstants,
    MarkdownRendererConstants,
} from '../constants';

import { ChatBlkTelemetryService } from '../services';
import { ChatBlkDispatch } from '../store/store';
import { sendQuery } from '../store/thunk';
import {
    Button,
    ButtonsBlock,
    QueryTypeEnum,
    telemetryFunctionMap,
} from '../types';
import { getKey, uuidv4 } from '../utils';

const NextActionContainer = styled.div`
    display: flex;
    min-width: 264px;
    padding: 24px;
    box-sizing: border-box;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    border-radius: 16px;
    background: #f4f1eb; // $blk-neutral-shade
`;

const NextActionContainerHeader = styled.div`
    font-family: 'BLK Fort';
    font-size: 14px;
    font-style: normal;
    font-weight: 800;
    color: #000;
    line-height: 18px;
    letter-spacing: 0;
    text-transform: uppercase;
    display: flex;
    gap: 16px;
`;

const NextAction = styled.div`
    padding: 0;
`;

const { DATA_CY_ACTION_BUTTONS } = ActionButtonRendererConstants;
/**
 * ActionButtonRenderer component renders a container of suggested next action links.
 *
 * @returns {JSX.Element} container of action links.
 * @component
 */
const ActionButtonRenderer = ({ buttons }: ButtonsBlock): JSX.Element => {
    const dispatch = useDispatch<ChatBlkDispatch>();

    const renderNextAction = (button: Button, buttonIndex: number) => {
        const { label, buttonType, uri, description } = button;
        const isContactRM: boolean = label === 'Contact RM';
        const dataLinkEvent = isContactRM
            ? { dataLinkEvent: 'chatblk | result | contact rm | open' }
            : {
                  dataLinkEvent: `chatblk | result | link click | ${label}`,
              };

        return (
            <NextAction key={getKey('button', buttonIndex)}>
                <BxfLink
                    label={label}
                    href={uri}
                    target={BxfTargetAtrEnum.BLANK}
                    {...dataLinkEvent}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        ChatBlkTelemetryService.track(
                            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_BUTTON_CLICKED,
                            { label, buttonType, uri, description }
                        );

                        if (isContactRM) {
                            dispatch(
                                sendQuery({
                                    prompt: label,
                                    chatExchangeId: uuidv4(),
                                    hashId: uuidv4(),
                                    queryType: QueryTypeEnum.CONTACT_RM,
                                    telemetryFunction:
                                        telemetryFunctionMap.CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_BUTTON_CLICKED,
                                })
                            );
                        } else if (uri) {
                            window.open(
                                uri,
                                MarkdownRendererConstants.BXF_LINK_TARGET_BLANK
                            );
                        }
                    }}
                />
                <br />
                <span>{description}</span>
            </NextAction>
        );
    };

    const renderButtons = () => {
        return buttons.map((button, row) => renderNextAction(button, row));
    };

    if (buttons?.length) {
        return (
            <NextActionContainer data-cy={DATA_CY_ACTION_BUTTONS}>
                <NextActionContainerHeader>
                    <BxfIcon type={BxfIconTypeEnum.lightbulb} />
                    What you can do next
                </NextActionContainerHeader>
                {renderButtons()}
            </NextActionContainer>
        );
    }
    return null;
};

export default ActionButtonRenderer;
